import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS } from '../appConst';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // Increased to 120s for heavy operations (analytics, PDF generation, image processing)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and disable caching
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Disable caching for all requests to ensure fresh data (critical for 24/7 operation)
    // Use headers only to avoid CORS preflight issues with query params
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    config.headers['Pragma'] = 'no-cache';
    config.headers['Expires'] = '0';

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // Clear auth data
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      // Redirect to login page
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Helper function to build URLs with path parameters
const buildUrl = (endpoint, pathParams = {}) => {
  let url = endpoint;
  Object.keys(pathParams).forEach((key) => {
    url = url.replace(`{${key}}`, pathParams[key]);
  });
  return url;
};

// ==================== AUTH APIs ====================

const login = async (username, password) => {
  try {
    const response = await apiClient.post('/auth/login', {
      username,
      password
    });

    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Login failed');
  }
};

const logout = async () => {
  try {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Logout failed');
  }
};

const getMe = async () => {
  try {
    const response = await apiClient.get('/auth/me');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to get user info');
  }
};

const checkAuth = async () => {
  try {
    const response = await apiClient.get('/auth/check');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Auth check failed');
  }
};

// ==================== USER MANAGEMENT APIs ====================

const getUsers = async (params = {}) => {
  try {
    const response = await apiClient.get('/api/users', { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch users');
  }
};

const createUser = async (userData) => {
  try {
    const response = await apiClient.post('/api/users', userData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to create user');
  }
};

const updateUser = async (userId, userData) => {
  try {
    const response = await apiClient.put(`/api/users/${userId}`, userData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to update user');
  }
};

const deleteUser = async (userId) => {
  try {
    const response = await apiClient.delete(`/api/users/${userId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to delete user');
  }
};

const getUserLoginHistory = async (userId, params = {}) => {
  try {
    const response = await apiClient.get(`/api/users/${userId}/login-history`, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch login history');
  }
};

const getUserSessions = async (userId) => {
  try {
    const response = await apiClient.get(`/api/users/${userId}/sessions`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch sessions');
  }
};

const revokeSession = async (sessionId) => {
  try {
    const response = await apiClient.post(`/api/sessions/${sessionId}/revoke`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to revoke session');
  }
};

const getUserActivity = async (userId, params = {}) => {
  try {
    const response = await apiClient.get(`/api/users/${userId}/activity`, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch user activity');
  }
};

const getUserActions = async (userId, params = {}) => {
  try {
    const response = await apiClient.get(`/api/users/${userId}/actions`, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch user actions');
  }
};

const getAuditTrail = async (params = {}) => {
  try {
    const response = await apiClient.get('/api/audit-trail', { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch audit trail');
  }
};

const createBulkUsers = async (users) => {
  try {
    const response = await apiClient.post('/api/users/bulk', { users });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to create bulk users');
  }
};

const exportUsers = async () => {
  try {
    const response = await apiClient.get('/api/users/export', {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to export users');
  }
};

const getProfile = async () => {
  try {
    const response = await apiClient.get('/api/profile');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch profile');
  }
};

const updateProfile = async (profileData) => {
  try {
    const response = await apiClient.put('/api/profile', profileData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to update profile');
  }
};

const changePassword = async (currentPassword, newPassword) => {
  try {
    const response = await apiClient.post('/api/profile/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to change password');
  }
};

const getUserStatistics = async () => {
  try {
    const response = await apiClient.get('/api/statistics/users');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch user statistics');
  }
};

// ==================== CAMERA APIs ====================

const getCameras = async () => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.CAMERAS);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch cameras');
  }
};

const getDisplayStatus = async () => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.DISPLAY_STATUS);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch display status');
  }
};

// ==================== UNKNOWN FACES APIs ====================

const getUnknownList = async (params = {}) => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.UNKNOWN_LIST, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch unknown faces');
  }
};

const getUnknownLogs = async (unknownId, params = {}) => {
  try {
    const url = buildUrl(API_ENDPOINTS.UNKNOWN_LOGS, { id: unknownId });
    const response = await apiClient.get(url, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch unknown logs');
  }
};

const getUnknownPhoto = (unknownId, photoIndex = 0) => {
  const url = buildUrl(API_ENDPOINTS.UNKNOWN_PHOTO, { id: unknownId });
  return `${API_BASE_URL}${url}?image_index=${photoIndex}`;
};

const getUnknownPhotos = async (unknownId) => {
  try {
    const url = buildUrl(API_ENDPOINTS.UNKNOWN_PHOTOS, { id: unknownId });
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch unknown photos');
  }
};

const getSuggestedMatches = async (unknownId, params = {}) => {
  try {
    const response = await apiClient.get(`/faces/unknown/${unknownId}/suggest-matches`, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch suggested matches');
  }
};

const mergeUnknownToEmployee = async (unknownId, empId) => {
  try {
    const response = await apiClient.post(`/faces/unknown/${unknownId}/merge-to-employee`, {
      emp_id: empId,
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to merge unknown face');
  }
};

const promoteUnknown = async (payload) => {
  try {
    // Backend expects FormData, not JSON
    const formData = new FormData();
    formData.append('unknown_id', payload.unknown_id);
    formData.append('emp_id', payload.emp_id);
    formData.append('mode', payload.mode);

    if (payload.mode === 'new') {
      formData.append('full_name', payload.full_name);
      if (payload.email) formData.append('email', payload.email);
    }

    const response = await apiClient.post(API_ENDPOINTS.PROMOTE_UNKNOWN, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to promote unknown face');
  }
};

const deleteUnknownFace = async (unknownId) => {
  try {
    const response = await apiClient.delete(`/faces/unknown/${unknownId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to delete unknown face');
  }
};

const deleteAllUnknownFaces = async () => {
  try {
    const response = await apiClient.delete('/faces/unknown');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to delete all unknown faces');
  }
};

const countBatchMergeEligible = async (minConfidence = 0.45) => {
  try {
    const response = await apiClient.get('/faces/unknown/batch-merge/count', {
      params: { min_confidence: minConfidence }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to count batch merge eligible faces');
  }
};

const batchMergeUnknownFaces = async (minConfidence = 0.45) => {
  try {
    const response = await apiClient.post('/faces/unknown/batch-merge', {
      min_confidence: minConfidence
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to batch merge unknown faces');
  }
};

const batchMergeSelectedUnknownFaces = async (mergeRequests) => {
  try {
    const response = await apiClient.post('/faces/unknown/batch-merge-selected', mergeRequests);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to batch merge selected unknown faces');
  }
};

// ==================== EMPLOYEE APIs ====================

const getEmployeeList = async (params = {}) => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.EMPLOYEE_LIST, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch employees');
  }
};

const searchEmployees = async (empId) => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.EMPLOYEE_SEARCH, {
      params: { emp_id: empId },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to search employee');
  }
};

const getEmployeeLogs = async (empId, params = {}) => {
  try {
    const url = buildUrl(API_ENDPOINTS.EMPLOYEE_LOGS, { id: empId });
    const response = await apiClient.get(url, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch employee logs');
  }
};

const getEmployeePhotos = async (empId) => {
  try {
    const url = buildUrl('/employees/{id}/photos', { id: empId });
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch employee photos');
  }
};

const getEmployeePhoto = (empId, filename = null, photoUpdatedAt = null) => {
  const url = buildUrl('/employees/{id}/photo', { id: empId });
  // Build URL with optional filename and cache-busting timestamp
  let photoUrl = `${API_BASE_URL}${url}`;
  const params = [];
  if (filename) params.push(`filename=${filename}`);
  if (photoUpdatedAt) params.push(`v=${photoUpdatedAt}`);
  if (params.length > 0) photoUrl += `?${params.join('&')}`;
  return photoUrl;
};

const setPrimaryPhoto = async (empId, filename) => {
  try {
    const url = buildUrl('/employees/{id}/primary-photo', { id: empId });
    const response = await apiClient.put(url, null, {
      params: { filename }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to set primary photo');
  }
};

const registerNewEmployee = async (formData) => {
  try {
    const response = await apiClient.post(API_ENDPOINTS.REGISTER_EMPLOYEE, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to register employee');
  }
};

const deleteEmployee = async (empId) => {
  try {
    const response = await apiClient.delete(`/employees/${empId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to delete employee');
  }
};

const addEmployeePhotos = async (empId, formData) => {
  try {
    const response = await apiClient.post(`/employees/${empId}/photos`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to add employee photos');
  }
};

const deleteEmployeePhoto = async (empId, filename) => {
  try {
    const response = await apiClient.delete(`/employees/${empId}/photos/${filename}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to delete employee photo');
  }
};

// ==================== REPORTS APIs ====================

const getDailyReport = async (empId, params = {}) => {
  try {
    const url = buildUrl(API_ENDPOINTS.DAILY_REPORT, { id: empId });
    const response = await apiClient.get(url, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch daily report');
  }
};

const getWeeklyReport = async (empId, params = {}) => {
  try {
    const url = buildUrl(API_ENDPOINTS.WEEKLY_REPORT, { id: empId });
    const response = await apiClient.get(url, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch weekly report');
  }
};

const getMonthlyReport = async (empId, params = {}) => {
  try {
    const url = buildUrl(API_ENDPOINTS.MONTHLY_REPORT, { id: empId });
    const response = await apiClient.get(url, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch monthly report');
  }
};

const getEmotionReport = async (params = {}) => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.EMOTION_REPORT, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch emotion report');
  }
};

// ==================== ANALYTICS APIs ====================

const getDashboardAnalytics = async (startDate = null, endDate = null) => {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await apiClient.get(API_ENDPOINTS.DASHBOARD_ANALYTICS, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch dashboard analytics');
  }
};

const getEmotionDistribution = async (params = {}) => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.EMOTION_DISTRIBUTION, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch emotion distribution');
  }
};

const getHourlyHeatmap = async (startDate = null, endDate = null, mode = 'range') => {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (mode) params.mode = mode;  // 'range' or 'today'

    const response = await apiClient.get('/analytics/hourly-heatmap', { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch hourly heatmap');
  }
};

const getCameraPerformance = async (startDate = null, endDate = null) => {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await apiClient.get('/analytics/camera-performance', { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch camera performance');
  }
};

const getTopActiveEmployees = async (limit = 10, startDate = null, endDate = null) => {
  try {
    const params = { limit };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await apiClient.get('/analytics/top-active-employees', { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch top active employees');
  }
};

const getEmotionTrends = async (startDate = null, endDate = null) => {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await apiClient.get('/analytics/emotion-trends', { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch emotion trends');
  }
};

const getComparativeAnalysis = async (startDate, endDate) => {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await apiClient.get('/analytics/comparative-analysis', { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch comparative analysis');
  }
};

const getQuickInsights = async () => {
  try {
    const response = await apiClient.get('/analytics/quick-insights');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch quick insights');
  }
};

const getEmployeesByEmotion = async (date = null) => {
  try {
    const params = {};
    if (date) params.date = date;
    const response = await apiClient.get('/analytics/employees-by-emotion', { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch employees by emotion');
  }
};

// ==================== CROSSINGS APIs ====================

const getCrossingsReport = async (empId = null, startDate = null, endDate = null, cameraId = null, thresholdSeconds = 30, limit = 1000) => {
  try {
    const params = {
      threshold_seconds: thresholdSeconds,
      limit,
    };

    if (empId) params.emp_id = empId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (cameraId) params.camera_id = cameraId;

    const response = await apiClient.get(API_ENDPOINTS.CROSSINGS_REPORT, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch crossings report');
  }
};

const getEmployeeCrossings = async (empId, startDate = null, endDate = null, thresholdSeconds = 30) => {
  try {
    const url = buildUrl(API_ENDPOINTS.CROSSINGS_EMPLOYEE, { id: empId });
    const params = { threshold_seconds: thresholdSeconds };

    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await apiClient.get(url, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch employee crossings');
  }
};

const getDailySummary = async (date = null) => {
  try {
    const params = {};
    if (date) params.date = date;

    const response = await apiClient.get(API_ENDPOINTS.CROSSINGS_DAILY_SUMMARY, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch daily summary');
  }
};

const getTopCrossers = async (limit = 10, startDate = null, endDate = null) => {
  try {
    const params = { limit };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await apiClient.get(API_ENDPOINTS.CROSSINGS_TOP_CROSSERS, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch top crossers');
  }
};

// ==================== LEGACY APIs ====================

const getLastSeen = async (limit = 10) => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.LAST_SEEN, {
      params: { limit },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch last seen');
  }
};

// Export all API methods
const api = {
  // Auth
  login,
  logout,
  getMe,
  checkAuth,

  // User Management
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  getUserLoginHistory,
  getUserSessions,
  revokeSession,
  getUserActivity,
  getUserActions,
  getAuditTrail,
  createBulkUsers,
  exportUsers,
  getProfile,
  updateProfile,
  changePassword,
  getUserStatistics,

  // Cameras
  getCameras,
  getDisplayStatus,

  // Unknown Faces
  getUnknownList,
  getUnknownLogs,
  getUnknownPhoto,
  getUnknownPhotos,
  getSuggestedMatches,
  mergeUnknownToEmployee,
  promoteUnknown,
  deleteUnknownFace,
  deleteAllUnknownFaces,
  countBatchMergeEligible,
  batchMergeUnknownFaces,
  batchMergeSelectedUnknownFaces,

  // Employees
  getEmployeeList,
  searchEmployees,
  getEmployeeLogs,
  getEmployeePhotos,
  getEmployeePhoto,
  setPrimaryPhoto,
  registerNewEmployee,
  deleteEmployee,
  addEmployeePhotos,
  deleteEmployeePhoto,

  // Reports
  getDailyReport,
  getWeeklyReport,
  getMonthlyReport,
  getEmotionReport,

  // Analytics
  getDashboardAnalytics,
  getEmotionDistribution,
  getHourlyHeatmap,
  getCameraPerformance,
  getTopActiveEmployees,
  getEmotionTrends,
  getComparativeAnalysis,
  getQuickInsights,
  getEmployeesByEmotion,

  // Crossings
  getCrossingsReport,
  getEmployeeCrossings,
  getDailySummary,
  getTopCrossers,

  // Legacy
  getLastSeen,
};

// Export both as default and named export for compatibility
export { api };
export default api;
